'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/General/Button'
import { toastEmitter } from '@/General/Toast'
import { useAddItemToCart } from '@/useAddItemToCart'

type BackColorTab = 'red' | 'blue'
type SuitGroup = 'Spades' | 'Hearts' | 'Clubs' | 'Diamonds' | 'Jokers' | 'Other'

type ProductOption = {
  id: number
  description: string
  price: number
  salePrice?: number | null
  currencySymbol: string
  inStock: boolean
  productType: string
}

interface OneWayForcingDeckOptionGridProps {
  productId: number
  productName: string
  options: ProductOption[]
  isPreorder?: boolean
}

export const OneWayForcingDeckOptionGrid = ({
  productId,
  productName: _productName,
  options,
  isPreorder = false,
}: OneWayForcingDeckOptionGridProps) => {
  const router = useRouter()
  const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([])
  const [isAddingSelected, setIsAddingSelected] = useState(false)
  const [activeTab, setActiveTab] = useState<BackColorTab>('red')

  const displayableOptions = useMemo(
    () =>
      options.filter(
        option =>
          option.id != null && option.productType?.toLowerCase() !== 'component'
      ),
    [options]
  )
  const getBackColor = (description: string): BackColorTab | null => {
    const normalized = description.toLowerCase()
    if (normalized.includes('blue back')) return 'blue'
    if (normalized.includes('red back')) return 'red'
    return null
  }
  const getDisplayDescription = (description: string) =>
    description
      .replace(/\s*-\s*red backed\b/i, '')
      .replace(/\s*-\s*blue backs?\b/i, '')
      .replace(/\s*-\s*red backs?\b/i, '')
      .replace(/\s+of\s+spades\b/i, ' ♠')
      .replace(/\s+of\s+hearts\b/i, ' ♥')
      .replace(/\s+of\s+clubs\b/i, ' ♣')
      .replace(/\s+of\s+diamonds\b/i, ' ♦')
      .trim()
  const filteredOptions = useMemo(
    () =>
      displayableOptions.filter(option => getBackColor(option.description) === activeTab),
    [activeTab, displayableOptions]
  )
  const getSuitGroup = (description: string): SuitGroup => {
    const normalized = description.toLowerCase()
    if (normalized.includes('joker')) return 'Jokers'
    if (normalized.includes(' of spades')) return 'Spades'
    if (normalized.includes(' of hearts')) return 'Hearts'
    if (normalized.includes(' of clubs')) return 'Clubs'
    if (normalized.includes(' of diamonds')) return 'Diamonds'
    return 'Other'
  }
  const groupedFilteredOptions = useMemo(() => {
    const grouped = new Map<SuitGroup, ProductOption[]>()
    for (const option of filteredOptions) {
      const suit = getSuitGroup(option.description)
      const existing = grouped.get(suit) ?? []
      grouped.set(suit, [...existing, option])
    }
    const order: SuitGroup[] = [
      'Spades',
      'Hearts',
      'Clubs',
      'Diamonds',
      'Jokers',
      'Other',
    ]
    return order
      .map(suit => ({
        suit,
        options: grouped.get(suit) ?? [],
      }))
      .filter(group => group.options.length > 0)
  }, [filteredOptions])
  const redCount = useMemo(
    () =>
      displayableOptions.filter(option => getBackColor(option.description) === 'red')
        .length,
    [displayableOptions]
  )
  const blueCount = useMemo(
    () =>
      displayableOptions.filter(option => getBackColor(option.description) === 'blue')
        .length,
    [displayableOptions]
  )

  const { addItem } = useAddItemToCart({
    onError: () => {
      toastEmitter.emit('toast', {
        title: 'Failed to add selected items',
        variant: 'error',
      })
    },
  })

  const toggleOption = (optionId: number, disabled: boolean) => {
    if (disabled) return
    setSelectedOptionIds(current =>
      current.includes(optionId)
        ? current.filter(id => id !== optionId)
        : [...current, optionId]
    )
  }

  const handleAddSelected = async () => {
    if (selectedOptionIds.length === 0 || isAddingSelected) return

    setIsAddingSelected(true)
    try {
      for (const optionId of selectedOptionIds) {
        await addItem({
          productId,
          productOptionId: optionId,
          quantity: 1,
        })
      }
      setSelectedOptionIds([])
      router.push('/checkout')
    } catch (error) {
      console.error('Bulk add failed for one-way forcing deck:', error)
    } finally {
      setIsAddingSelected(false)
    }
  }

  return (
    <section
      id="one-way-option-picker"
      className="mt-8 border border-gray-200 rounded-md bg-white p-4 md:p-6 scroll-mt-24"
    >
      <h3 className="text-xl font-semibold">
        Choose your one-way Bicycle forcing decks.
      </h3>
      <p className="text-sm text-gray-600 mt-1 mb-4">
        All decks are genuine Bicycle Rider Back decks.
      </p>

      <div className="mb-5 border-b border-gray-200">
        <div className="flex flex-wrap gap-2 -mb-px">
        <button
          type="button"
          onClick={() => setActiveTab('red')}
          className={`px-5 py-3 text-base font-semibold rounded-t-lg border transition ${
            activeTab === 'red'
              ? 'bg-red-50 border-red-500 border-b-red-50 text-red-700 shadow-sm'
              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-white hover:border-gray-300'
          }`}
        >
          Red Bicycle Rider Backs ({redCount})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('blue')}
          className={`px-5 py-3 text-base font-semibold rounded-t-lg border transition ${
            activeTab === 'blue'
              ? 'bg-blue-50 border-blue-500 border-b-blue-50 text-blue-700 shadow-sm'
              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-white hover:border-gray-300'
          }`}
        >
          Blue Bicycle Rider Backs ({blueCount})
        </button>
      </div>
      </div>

      <div className="space-y-5">
        {groupedFilteredOptions.map((group, groupIndex) => (
          <div
            key={group.suit}
            className={
              groupIndex < groupedFilteredOptions.length - 1
                ? 'pb-5 border-b border-gray-200'
                : ''
            }
          >
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-600 mb-2">
              {group.suit}{' '}
              <span className="normal-case font-medium text-gray-500">
                ({activeTab === 'red' ? 'Red back' : 'Blue back'})
              </span>
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
              {group.options.map(option => {
                const isDisabled = !option.inStock && !isPreorder
                const isSelected = selectedOptionIds.includes(option.id)
                const suit = getSuitGroup(option.description)
                const isRedSuit = suit === 'Hearts' || suit === 'Diamonds'
                const effectivePrice =
                  option.salePrice != null && option.salePrice > 0
                    ? option.salePrice
                    : option.price

                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => toggleOption(option.id, isDisabled)}
                    aria-pressed={isSelected}
                    className={`text-left border rounded-md px-2 py-2 transition ${
                      isDisabled
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : isSelected
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-300 hover:border-green-500'
                    }`}
                  >
                    <div
                      className={`font-semibold text-xl leading-tight ${
                        isRedSuit ? 'text-red-600' : ''
                      }`}
                    >
                      {getDisplayDescription(option.description)}
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">
                      {option.currencySymbol}
                      {effectivePrice.toFixed(2)}
                    </div>
                    {isDisabled && (
                      <div className="text-xs text-gray-500 mt-1">
                        Out of stock
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      {filteredOptions.length === 0 && (
        <div className="text-sm text-gray-600 py-2">
          No options found for this back color.
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="text-sm text-gray-700">
          {selectedOptionIds.length} selected
        </div>
        <Button
          className="sm:ml-auto"
          color="green"
          disabled={selectedOptionIds.length === 0 || isAddingSelected}
          onClick={handleAddSelected}
        >
          {`Add selected ${selectedOptionIds.length} decks to cart`}
        </Button>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Each selected option is added with quantity 1. Add additional
        quantities on cart page.
      </p>
    </section>
  )
}
